import { Box, Card, CardContent, CardHeader, Grid, Button, Autocomplete, IconButton, Paper, TextField, Tooltip, Typography } from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import TableChartIcon from '@mui/icons-material/TableChart';
import SaveIcon from '@mui/icons-material/Save';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import EditIcon from '@mui/icons-material/Edit';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FilterListIcon from '@mui/icons-material/FilterList';
import LastPageIcon from '@mui/icons-material/LastPage';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RemoveIcon from '@mui/icons-material/Remove';
import * as XLSX from 'xlsx'
import AddBoxIcon from '@mui/icons-material/AddBox';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import MaterialTable from 'material-table'
import axios from 'src/utils/axios';
import { forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUser } from 'src/utils/Common';
import { makeStyles } from '@mui/styles';
import React, { useState, useEffect } from 'react';
import { getRegions } from 'src/store/slices/assigmentSlice';
import AlertAndLoading from 'src/components/AlertAndLoading';

const ORANGE = '#FF9800';
const DARK = '#424242';

const darkButtonSx = {
  color: ORANGE,
  backgroundColor: DARK,
  borderRadius: '10px',
  fontWeight: 600,
  fontSize: '13px',
  height: '36px',
  '&:hover': { backgroundColor: '#212121' },
  '&.Mui-disabled': { backgroundColor: '#e0e0e0', color: '#9e9e9e' }
};

const inputSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: '#fff', fontSize: '13px' },
  '& .MuiInputLabel-root': { fontSize: '13px' }
};

const listboxProps = { sx: { fontSize: '13px' } };

const useStyles = makeStyles((theme) => ({
  root: {
    fontFamily: "Segoe UI",
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '16px 24px',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      padding: '12px'
    }
  },
  card: {
    border: '1px solid #e0e0e0',
    borderRadius: '16px',
    backgroundColor: '#fff',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    overflow: 'hidden'
  },
  cardHeader: {
    backgroundColor: '#f5f5f5',
    color: DARK,
    padding: '10px 16px',
    borderBottom: '1px solid #e0e0e0',
    '& .MuiCardHeader-title': {
      fontSize: '12px',
      fontWeight: '700',
      letterSpacing: '0.5px',
      textTransform: 'uppercase'
    },
    '& .MuiCardHeader-subheader': {
      fontSize: '11px',
      color: '#757575'
    }
  },
  fileBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    height: '36px',
    padding: '0 12px',
    borderRadius: '10px',
    border: `1px dashed ${ORANGE}`,
    backgroundColor: '#fffaf2',
    cursor: 'pointer',
    boxSizing: 'border-box',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#fff3e0'
    }
  },
  infoBox: {
    padding: '8px 12px',
    backgroundColor: '#fafafa',
    borderRadius: '10px',
    border: '1px solid #e0e0e0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    position: 'relative',
    overflow: 'hidden',
    textAlign: 'center',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '3px',
      backgroundColor: ORANGE
    }
  },
  infoLabel: {
    fontWeight: '600',
    fontSize: '10px',
    color: '#546e7a',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  infoValue: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#263238'
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 16px'
  },
  tableWrapper: {
    '& .MuiTableCell-root': {
      fontSize: '12px'
    },
    '& .MuiTableRow-root:hover': {
      backgroundColor: '#fff8e1'
    },
    '& .MuiToolbar-root': {
      minHeight: '48px'
    }
  }
}));


const tableIcons = {
  Add: forwardRef((props, ref) => <AddBoxIcon {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <CheckIcon {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <ClearIcon {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutlineIcon {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => <ChevronRightIcon {...props} ref={ref} />),
  Edit: forwardRef((props, ref) => <EditIcon {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAltIcon {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterListIcon {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPageIcon {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPageIcon {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRightIcon {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => <ChevronLeftIcon {...props} ref={ref} />),
  ResetSearch: forwardRef((props, ref) => <ClearIcon {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <SearchIcon {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowDownwardIcon {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <RemoveIcon {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumnIcon {...props} ref={ref} />)
};

const EXTENSIONS = ['xlsx', 'xls', 'csv']
const columns = ["Stok", "StokMiktarı", "Bölge", "Yükleyen"]

function StockExcelUpload() {
  const classes = useStyles();
  const SessionUser = getUser();
  const dispatch = useDispatch();

  const regions = useSelector((state) => state.assigmentSlice.regions);

  const [selectedRegion, setSelectedRegion] = React.useState(null);
  const [saveFileData, setSaveFileData] = useState([]);
  const [colDefs, setColDefs] = useState()
  const [loading, setLoading] = React.useState(false);
  const [fileName, setFileName] = useState('');

  const [responseMessage, setresponseMessage] = React.useState('');
  const [uyari, setUyari] = React.useState(false);
  const [alertType, setAlertType] = React.useState({ ErrorType: 'error', ErrorTitle: 'Uyarı' });

  function InfoAlert(ErrorDescription, ErrorType, ErrorTitle) {
    setresponseMessage(ErrorDescription);
    setAlertType({
      ErrorType: ErrorType,
      ErrorTitle: ErrorTitle
    })
    setUyari(true);
    return
  }

  const uyariKapat = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setUyari(false);
  };

  useEffect(() => {
    dispatch(getRegions());
  }, [dispatch]);

  const getExention = (file) => {
    const parts = file.name.split('.')
    const extension = parts[parts.length - 1]
    return EXTENSIONS.includes(extension) // return boolean
  }

  const importExcel = (e) => {
    const files = e.target.files;
    if (files.length) {
      const file = files[0];
      setFileName(file.name);
      const reader = new FileReader();
      setLoading(true)
      if (getExention(file)) {
        reader.readAsBinaryString(file)
      }
      reader.onload = (event) => {
        //parse data
        const bstr = event.target.result
        const workBook = XLSX.read(bstr, { type: "binary" })
        //get first sheet
        const workSheetName = workBook.SheetNames[0]
        const workSheet = workBook.Sheets[workSheetName]
        //convert to array
        const fileData = XLSX.utils.sheet_to_json(workSheet, { header: 1 })
        const headers = fileData[0]
        const tempResult = []
        for (var i = 1; i < fileData.length; i++) {
          const tmp = {
            Stok: String(fileData[i][0]),
            StokMik: fileData[i][1],
            Bölge: selectedRegion ? selectedRegion : SessionUser.TopServiceShopCode,
            UserId: SessionUser.UserId
          }
          tempResult.push(tmp)
        }
        findTheDifference(tempResult)
        setColDefs(columns.map(head => ({ title: head, field: head })))
        setLoading(false)
        fileData.splice(0, 1)
      }
    }
  }
  const findTheDifference = (data) => {
    var changedValues = []
    data.map(i => {
      try {
        changedValues.push(
          {
            Stok: i.Stok,
            StokMiktarı: i.StokMik,
            Bölge: selectedRegion ? selectedRegion : SessionUser.TopServiceShopCode,
            Yükleyen: SessionUser.UserId
          }
        );
      } catch (ex) {
      }
    })
    setSaveFileData(changedValues);
  }

  const dfSave = () => {
    setLoading(true)
    var data = [];
    saveFileData.map(i => {
      try {
        data.push(
          {
            ProductCode: i.Stok,
            Count: i.StokMiktarı,
            TopRegionId: selectedRegion ? selectedRegion : SessionUser.TopServiceShopCode,
            UserId: SessionUser.UserId
          }
        );
      } catch (ex) {
        setLoading(false)
      }
    })
    axios.post('SetProductAvalibleCount', data, {
      headers: { 'Content-Type': 'application/json' }
    }).then(response => {
      setSaveFileData([])
      setFileName('')
      // setresponseMessage({
      //   ErrorCode: 1000,
      //   ErrorDescription: 'İşleminiz başarılıyla gerçekleşmiştir...',
      // });
      // setUyari(true);
      InfoAlert('İşleminiz başarılıyla gerçekleşmiştir...', 'success', 'Başarılı');
      setLoading(false)
      return
    }).catch(err => {
      InfoAlert('Kaydedilemedi... Lütfen daha sonra tekrar deneyiniz...', 'error', 'Hata');
      // setresponseMessage({
      //   ErrorCode: 0,
      //   ErrorDescription: 'Kaydedilemedi... Lütfen daha sonra tekrar deneyiniz...',
      // });
      // setUyari(true);
      setLoading(false)
      return
    });
  }

  const clearFile = () => {
    setSaveFileData([]);
    setColDefs([]);
    setFileName('');
  };

  const selectedRegionItem =regions.find(i => i.Id == selectedRegion) || regions.find(i => i.Id == SessionUser.TopServiceShopCode);
  const totalCount = saveFileData.reduce((sum, i) => sum + (Number(i.StokMiktarı) || 0), 0);
  const summaryItems = [
    { label: 'Bölge', value: selectedRegionItem ? selectedRegionItem.Dsc : SessionUser.TopServiceShopCode },
    { label: 'Dosya', value: fileName || '-' },
    { label: 'Yüklenen Satır', value: saveFileData.length },
    { label: 'Toplam Stok Adedi', value: totalCount.toLocaleString('tr-TR') }
  ];

  return (
    <>
      <AlertAndLoading Message={responseMessage} open={uyari} AlertType={alertType} onClose={uyariKapat} loading={loading} />
      <Box className={classes.root}>
        {/* Yükleme */}
        <Card className={classes.card}>
          <CardHeader
            className={classes.cardHeader}
            avatar={<InventoryIcon sx={{ color: ORANGE }} />}
            title="Stok Adet Yükleme"
            subheader="Bölge seçip Excel dosyasını yükleyin; ilk kolon stok kodu, ikinci kolon stok miktarı olmalıdır"
          />
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} sm={5} md={3} lg={2.5}>
                <Autocomplete
                  id="combo-box-demo"
                  options={regions}
                  getOptionLabel={(option) => option.Dsc}
                  size="small"
                  fullWidth
                  ListboxProps={listboxProps}
                  value={regions.find(i => i.Id == selectedRegion) || regions.find(i => i.Id == SessionUser.TopServiceShopCode)}
                  onChange={(event, val) => {
                    var value = val?.Id ? val.Id.toString() : SessionUser.TopServiceShopCode.toString()
                    setSelectedRegion(value);
                    setSaveFileData([]);
                    setColDefs([]);
                    setFileName('');
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Bölge" variant="outlined" sx={inputSx} />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={7} md={5} lg={4}>
                <input
                  id="FileInput"
                  type='file'
                  onChange={importExcel}
                  onClick={(event) => { event.target.value = null }}
                  hidden
                  accept=".xlsx, .xls, .xml"
                >
                </input>
                <label htmlFor="FileInput">
                  <Box className={classes.fileBox}>
                    <UploadFileIcon sx={{ color: ORANGE, fontSize: 20 }} />
                    <Typography noWrap sx={{ fontSize: '13px', color: fileName ? '#263238' : '#90a4ae', fontWeight: fileName ? 600 : 400, flex: 1 }}>
                      {fileName || 'Excel dosyası seçin (.xlsx, .xls)'}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: ORANGE, whiteSpace: 'nowrap' }}>
                      {fileName ? 'Değiştir' : 'Dosya Seç'}
                    </Typography>
                    {fileName && (
                      <Tooltip title="Temizle">
                        <IconButton
                          size="small"
                          sx={{ p: 0.25, color: '#90a4ae', '&:hover': { color: '#c62828' } }}
                          onClick={(e) => {
                            // label içinde olduğu için dosya seçme penceresini açmasın
                            e.preventDefault();
                            e.stopPropagation();
                            clearFile();
                          }}
                        >
                          <ClearIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </label>
              </Grid>
              <Grid item xs={12} sm={4} md={2} lg={1.5} sx={{ ml: { md: 'auto' } }}>
                <Button
                  fullWidth
                  startIcon={<SaveIcon />}
                  sx={darkButtonSx}
                  disabled={saveFileData.length === 0}
                  onClick={dfSave}
                >
                  Kaydet
                </Button>
              </Grid>
            </Grid>

            <Grid container spacing={1.5} sx={{ mt: 1 }}>
              {summaryItems.map((item) => (
                <Grid item xs={6} md={3} key={item.label}>
                  <Box className={classes.infoBox}>
                    <Typography className={classes.infoLabel}>{item.label}</Typography>
                    <Typography className={classes.infoValue} noWrap>{item.value}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        {/* Önizleme */}
        <Card className={classes.card}>
          <CardHeader
            className={classes.cardHeader}
            avatar={<TableChartIcon sx={{ color: ORANGE }} />}
            title="Stok Adetleri"
            subheader={saveFileData.length > 0 ? 'Kaydetmeden önce yüklenen satırları kontrol edin' : undefined}
          />
          {saveFileData.length === 0 ? (
            <Box className={classes.emptyState}>
              <UploadFileIcon sx={{ fontSize: 44, color: '#cfd8dc' }} />
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#78909c', mt: 1 }}>
                Henüz dosya yüklenmedi
              </Typography>
              <Typography sx={{ fontSize: '12px', color: '#90a4ae' }}>
                Yüklediğiniz dosyadaki satırlar burada listelenecek.
              </Typography>
            </Box>
          ) : (
            <Box className={classes.tableWrapper}>
              <MaterialTable
                title=""
                data={saveFileData}
                columns={colDefs}
                icons={tableIcons}
                components={{ Container: (props) => <Paper {...props} elevation={0} /> }}
                options={{
                  pageSize: 10,
                  pageSizeOptions: [10, 25, 50, 100],
                  headerStyle: { backgroundColor: '#f5f5f5', color: DARK, fontSize: '11px', fontWeight: 700, padding: '10px 16px' },
                  rowStyle: { fontSize: '12px' },
                  searchFieldStyle: { fontSize: '13px' }
                }}
                localization={{
                  toolbar: { searchPlaceholder: 'Ara' },
                  pagination: { labelRowsSelect: 'satır', labelDisplayedRows: '{from}-{to} / {count}' },
                  body: { emptyDataSourceMessage: 'Kayıt bulunamadı' }
                }}
              />
            </Box>
          )}
        </Card>
      </Box>
    </>
  );
}

export default StockExcelUpload;
